import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import SelectMenu from "@/components/SelectMenu";
import { fmtUSD, fmtPct, fmtNum, timeAgo } from "@/lib/format";
import { Search } from "lucide-react";

export default function Markets() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("active");
  const [sort, setSort] = useState("volume_24h");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["markets", { q, category, status, sort, page }],
    queryFn: async () => (
      await api.get("/markets", { params: { q: q || undefined, category: category || undefined,
                                              status, sort, page, page_size: 25 } })
    ).data,
    keepPreviousData: true,
  });

  const { data: cats } = useQuery({
    queryKey: ["market-categories"],
    queryFn: async () => (await api.get("/markets/categories")).data,
  });

  return (
    <div className="space-y-4" data-testid="markets-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Markets</div>
        <h1 className="font-heading text-2xl font-bold text-zinc-100">Explorer</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-auto">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            data-testid="input-market-search"
            placeholder="Search markets…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            className="pl-8 h-9 w-full sm:w-64 bg-zinc-900 border-zinc-800 text-sm"
          />
        </div>
        <SelectMenu
          testid="select-category"
          value={category}
          onChange={(v) => { setCategory(v); setPage(1); }}
          placeholder="All categories"
          className="w-full sm:w-56"
          options={[
            { value: "", label: "All categories" },
            ...(cats || []).map((c) => ({ value: c.category, label: `${c.category} (${c.count})` })),
          ]}
        />
        <SelectMenu
          testid="select-status"
          value={status}
          onChange={(v) => { setStatus(v); setPage(1); }}
          className="w-full sm:w-32"
          options={[
            { value: "active", label: "Active" },
            { value: "closed", label: "Closed" },
            { value: "resolved", label: "Resolved" },
            { value: "all", label: "All" },
          ]}
        />
        <SelectMenu
          testid="select-sort"
          value={sort}
          onChange={(v) => { setSort(v); setPage(1); }}
          className="w-full sm:w-48"
          options={[
            { value: "volume_24h", label: "Sort: Volume 24h" },
            { value: "volume", label: "Sort: Total volume" },
            { value: "liquidity", label: "Sort: Liquidity" },
            { value: "end_date", label: "Sort: End date" },
            { value: "first_seen_at", label: "Sort: Newest" },
          ]}
        />
      </div>

      {/* Table */}

      <div className="grid gap-3 md:hidden">
        {isLoading && <div className="rounded-md border border-zinc-800 p-4 text-center text-xs text-zinc-500">Loading…</div>}
        {!isLoading && data?.items?.length === 0 && <div className="rounded-md border border-zinc-800 p-4 text-center text-xs text-zinc-500">No markets match your filters.</div>}
        {(data?.items || []).map((m) => (
          <Link key={m.polymarket_market_id} to={`/app/markets/${m.polymarket_market_id}`} className="rounded-md border border-zinc-800 bg-zinc-950/40 p-3 hover:bg-zinc-900/40" data-testid={`market-card-${m.polymarket_market_id}`}>
            <div className="text-sm font-medium text-zinc-100 line-clamp-2">{m.title}</div>
            <div className="mt-1 text-[11px] text-zinc-500">{m.category || "Uncategorized"} · {m.status || "—"}</div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div className="rounded border border-zinc-800 p-2"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Probability</div><div className="font-mono-num text-zinc-100">{m.outcomes?.[0]?.price != null ? fmtPct(m.outcomes[0].price) : "—"}</div></div>
              <div className="rounded border border-zinc-800 p-2"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Volume</div><div className="font-mono-num text-zinc-100">{fmtUSD(m.volume)}</div></div>
              <div className="rounded border border-zinc-800 p-2"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Traders</div><div className="font-mono-num text-zinc-100">{fmtNum(m.tracked_trader_count)}</div></div>
              <div className="rounded border border-zinc-800 p-2"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Signals</div><div className="font-mono-num text-zinc-100">{fmtNum(m.signal_count)}</div></div>
            </div>
          </Link>
        ))}
      </div>
      <div className="hidden md:block border border-zinc-800 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-zinc-900/40 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2">Market</th>
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-right px-3 py-2">Probability</th>
                <th className="text-right px-3 py-2">Volume</th>
                <th className="text-right px-3 py-2">Liquidity</th>
                <th className="text-right px-3 py-2">Traders</th>
                <th className="text-right px-3 py-2">Signals</th>
                <th className="text-right px-3 py-2">End</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={8} className="text-xs text-zinc-500 text-center py-6">Loading…</td></tr>
              )}
              {!isLoading && data?.items?.length === 0 && (
                <tr><td colSpan={8} className="text-xs text-zinc-500 text-center py-6">No markets match your filters.</td></tr>
              )}
              {(data?.items || []).map((m) => (
                <tr key={m.polymarket_market_id} className="border-t border-zinc-800/70 hover:bg-zinc-900/40 transition-colors"
                    data-testid={`market-row-${m.polymarket_market_id}`}>
                  <td className="px-3 py-2 max-w-md">
                    <Link to={`/app/markets/${m.polymarket_market_id}`} className="text-zinc-100 hover:underline line-clamp-1">
                      {m.title}
                    </Link>
                    {m.is_demo && (
                      <span className="ml-2 text-[9px] uppercase text-orange-400">demo</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-zinc-400 text-xs">{m.category || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-num">
                    {m.outcomes?.[0]?.price != null ? fmtPct(m.outcomes[0].price) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono-num">{fmtUSD(m.volume)}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{fmtUSD(m.liquidity)}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{fmtNum(m.tracked_trader_count)}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{fmtNum(m.signal_count)}</td>
                  <td className="px-3 py-2 text-right text-[11px] text-zinc-500">
                    {m.end_date ? new Date(m.end_date).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-xs text-zinc-500">
          <div>
            Showing {(data.page - 1) * data.page_size + 1}–
            {Math.min(data.page * data.page_size, data.total)} of {data.total}
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="btn-prev-page"
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={data.page === 1}
              className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-200 h-8"
            >
              Prev
            </Button>
            <Button
              data-testid="btn-next-page"
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={data.page * data.page_size >= data.total}
              className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-200 h-8"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
